import { FormState } from '@navstation-portal/reporting/data-model/vessel-report';
import { ConsumptionsVm } from '@navstation-portal/reporting/data-model/vessel-report';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { getFieldValueFromFormState, isSectionValid, isStateField } from '../vm-builder-helpers';

export function buildConsumptionsVm(
  formState: FormState,
  powerFormState: FormState,
  vesselSpec: VesselSpec | undefined
): ConsumptionsVm {
  const calculateCylinderOilKgConsumption = (formState: FormState) => {
    const actualConsumptionLitres = getFieldValueFromFormState(
      formState,
      'cylinderOilConsumption',
      'oilConsumption'
    ) as number;
    if (actualConsumptionLitres == null || isNaN(actualConsumptionLitres)) return 0;

    const densityAt15 = getFieldValueFromFormState(formState, 'cylinderOilConsumption', 'oilDensityAt15') as number;
    if (densityAt15 == null || isNaN(densityAt15)) return 0;

    const tankTemperature = getFieldValueFromFormState(
      formState,
      'cylinderOilConsumption',
      'oilTankTemperature'
    ) as number;
    if (tankTemperature == null || isNaN(tankTemperature)) return 0;

    return ((actualConsumptionLitres * densityAt15) / 1000) * (1 - (tankTemperature - 15) * 0.00064);
  };

  const calculateCylinderOilSloc = (formState: FormState, oilKgConsumption: number) => {
    const mainEnginePower = getFieldValueFromFormState(powerFormState, 'mainEngine', 'power') as number;
    if (mainEnginePower == null || isNaN(mainEnginePower)) return 0;

    const mainEngineRhDiff = getFieldValueFromFormState(
      powerFormState,
      'machineryRunningHours',
      'mainEngines',
      0,
      'difference'
    ) as number;
    // V1 gives 0 while the running hours are missing or 0, so the division
    // gives no NaN and no Infinity.
    if (mainEngineRhDiff == null || isNaN(mainEngineRhDiff) || mainEngineRhDiff === 0) return 0;

    // TODO #144424 TCRPM
    const tcRpmPower = undefined;
    const useTcRpmPower = vesselSpec?.information.use_tcrpm_power;
    const power = useTcRpmPower ? tcRpmPower : mainEnginePower;
    if (power == null || power === 0) return 0;

    return (oilKgConsumption * 1000) / (power * mainEngineRhDiff);
  };

  const calculateTotal = (index: number) => {
    const section = formState['totalConsumptions'];
    if (!section) return;

    const entries = section['entries'];
    if (!entries || isStateField(entries)) return;

    const entry = entries[index];
    if (!entry) return;

    let total = 0;
    Object.keys(entry).forEach((key) => {
      if (key === 'cargoRelated' || key === 'total') return;

      const value = entry[key].value as number;
      if (value != null && !isNaN(value)) total += value;
    });
    return total;
  };

  // V1 sums the cargo consumers of one fuel (tabs.directive.ts:4638-4656).
  // V2 holds no crane operations, CDL operations, cargo hold washing or tank
  // pressure control rows, so the sum leaves them out. Cargo discharging is
  // V1's cargo shifting.
  const CARGO_TOTALS = ['igGenerator', 'otherEngines'];
  const CARGO_BREAKDOWN_ITEMS = [
    'cargoHeating',
    'tankCleaning',
    'cargoDischarging',
    'inertGasSystems',
    'bunkerHeating',
  ];

  const calculateCargoRelated = (index: number) => {
    const entryOf = (sectionId: string) => {
      const entries = formState[sectionId]?.['entries'];
      return !entries || isStateField(entries) ? undefined : entries[index];
    };

    const totals = entryOf('totalConsumptions');
    if (!totals) return;

    const consumers = [
      ...CARGO_TOTALS.map((key) => totals[key]),
      ...CARGO_BREAKDOWN_ITEMS.map((key) => entryOf('auxEngineConsumptions')?.[key]),
      ...CARGO_BREAKDOWN_ITEMS.map((key) => entryOf('boilerConsumptions')?.[key]),
    ];
    return consumers.reduce((sum, field) => {
      const value = field?.value as number | undefined;
      return value != null && !isNaN(value) ? sum + value : sum;
    }, 0);
  };

  const calculateAEBasicLoad = (index: number) => {
    const tcSection = formState['totalConsumptions'];
    if (!tcSection) return 0;

    const tcEntries = tcSection['entries'];
    if (!tcEntries || isStateField(tcEntries)) return 0;

    const tcEntry = tcEntries[index];
    if (!tcEntry) return 0;

    const aeSection = formState['auxEngineConsumptions'];
    if (!aeSection) return 0;

    const aeEntries = aeSection['entries'];
    if (!aeEntries || isStateField(aeEntries)) return 0;

    const aeEntry = aeEntries[index];
    if (!aeEntry) return 0;

    const totalAeConsumption = (tcEntry['auxEngines'].value as number) || 0;
    if (totalAeConsumption == null || isNaN(totalAeConsumption)) return 0;

    let aeBreakdown = 0;
    Object.keys(aeEntry).forEach((key) => {
      if (key === 'basicLoad') return;
      const value = aeEntry[key].value as number;
      if (value != null && !isNaN(value)) aeBreakdown += value;
    });

    return totalAeConsumption - aeBreakdown;
  };

  const calculateABBasicLoad = (index: number) => {
    const tcSection = formState['totalConsumptions'];
    if (!tcSection) return 0;

    const tcEntries = tcSection['entries'];
    if (!tcEntries || isStateField(tcEntries)) return 0;

    const tcEntry = tcEntries[index];
    if (!tcEntry) return 0;

    const abSection = formState['boilerConsumptions'];
    if (!abSection) return 0;

    const abEntries = abSection['entries'];
    if (!abEntries || isStateField(abEntries)) return 0;

    const abEntry = abEntries[index];
    if (!abEntry) return 0;

    const totalAbConsumption = (tcEntry['boiler'].value as number) || 0;
    if (totalAbConsumption == null || isNaN(totalAbConsumption)) return 0;

    let abBreakdown = 0;
    Object.keys(abEntry).forEach((key) => {
      if (key === 'basicLoad') return;
      const value = abEntry[key].value as number;
      if (value != null && !isNaN(value)) abBreakdown += value;
    });

    return totalAbConsumption - abBreakdown;
  };

  const isWaterValid = isSectionValid(formState['water']);
  const isInertGasValid = isSectionValid(formState['inertGasSystems']);
  const isCylinderOilValid = isSectionValid(formState['cylinderOilConsumption']);
  const isFuelSpecsValid = isSectionValid(formState['fuelSpecs']);
  const isTotalConsumptionsValid = isSectionValid(formState['totalConsumptions']);
  const isAuxEngineConsumptionsValid = isSectionValid(formState['auxEngineConsumptions']);
  const isBoilerConsumptionsValid = isSectionValid(formState['boilerConsumptions']);

  const isConsumptionsValid =
    isWaterValid &&
    isInertGasValid &&
    isCylinderOilValid &&
    isFuelSpecsValid &&
    isTotalConsumptionsValid &&
    isAuxEngineConsumptionsValid &&
    isBoilerConsumptionsValid;

  const cylinderOilKgConsumption = calculateCylinderOilKgConsumption(formState);
  const cylinderOilSloc = calculateCylinderOilSloc(formState, cylinderOilKgConsumption);

  return {
    isConsumptionsValid,
    cylinderOilKgConsumption,
    cylinderOilSloc,
    calculateAEBasicLoad,
    calculateABBasicLoad,
    calculateTotal,
    calculateCargoRelated,
  };
}
